import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Send } from 'lucide-react';

// Lead capture — posts enquiries into the Solar Swytch Inbox (api.solarswytch.com).
const LEADS_ENDPOINT = 'https://api.solarswytch.com/api/leads/ingest';
const SITE_ID = 'ektapower';
const SITE_API_KEY = 'sk_eP_e2358d0e1a6784f52cc2a90035aa4a2f5095d21624e379d0';

const EnquiryForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    siteType: '',
    systemSize: '',
    city: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, siteType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.fullName || !formData.email || !formData.phone || !formData.siteType) {
      toast.error('Please fill all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Phone validation (10 digits)
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(formData.phone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(LEADS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: SITE_ID,
          api_key: SITE_API_KEY,
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          site_type: formData.siteType,
          system_size: formData.systemSize,
          city: formData.city,
          message: formData.message
        })
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok && result.ok) {
        toast.success('Thank you! Our solar experts will contact you within 24 hours.');
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          siteType: '',
          systemSize: '',
          city: '',
          message: ''
        });
      } else {
        toast.error(result?.error?.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Get Your Free Solar Quote
            </h2>
            <p className="text-lg text-gray-600">
              Fill in your details and our solar experts will contact you within 24 hours
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100" style={{borderTop: '4px solid #2CC84B', background: 'linear-gradient(to bottom, #f0fdf4 0%, white 8%) no-repeat, white'}}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Contact Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="10-digit mobile number"
                    maxLength={10}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="siteType">Type of Site *</Label>
                  <Select onValueChange={handleSelectChange} value={formData.siteType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select site type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="domestic">Domestic / Residential</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="industrial">Industrial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="systemSize">Required System Size (kW)</Label>
                  <Input
                    id="systemSize"
                    name="systemSize"
                    value={formData.systemSize}
                    onChange={handleChange}
                    placeholder="e.g., 3, 5, 10"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">City / Location</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Enter your city"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Additional Requirements (Optional)</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your energy needs, roof type, or any specific requirements..."
                  rows={4}
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="ep-shimmer-btn w-full text-white text-lg py-6"
              >
                {isSubmitting ? (
                  'Submitting...'
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Request Free Quote
                  </>
                )}
              </Button>

              <p className="text-sm text-gray-500 text-center">
                By submitting this form, you agree to our privacy policy and terms of service.
              </p>
            </form>
          </div>

          {/* Trust Badges */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="text-2xl font-bold" style={{color: '#2CC84B'}}>500+</p>
              <p className="text-sm text-gray-600">Happy Customers</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="text-2xl font-bold" style={{color: '#3F51B5'}}>5MW+</p>
              <p className="text-sm text-gray-600">Installed Capacity</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="text-2xl font-bold" style={{color: '#2CC84B'}}>25 Years</p>
              <p className="text-sm text-gray-600">Panel Warranty</p>
            </div>
            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
              <p className="text-2xl font-bold" style={{color: '#00BCD4'}}>Certified</p>
              <p className="text-sm text-gray-600">Govt Approved</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnquiryForm;
